import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Panel,
  PanelHeader,
  PanelBody,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  SubTitleLarge,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import ResumeCard from 'src/components/crm/jobs/job-info/overview/feedbacks/panels/resume/resume';
import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import {
  selectJobInformation,
  selectApplicantInformation,
  selectJobInterviewFlow,
  selectFilteredJobList,
} from 'src/selectors/crm';
import {useInflight} from 'src/hooks/redux';
import {debounce} from 'lodash';
import {
  getApplicantInformation,
  getJobInformation,
  updateCrmApplicantInformationData,
  getInterviewFlow,
} from 'src/action-creators/crm';
import {getFilteredJob} from 'src/action-creators/crm-new';
import {useSelector, useDispatch} from 'react-redux';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {useHistory, useParams} from 'src/rerouter';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import * as reduxApi from 'src/utils/redux-api-v2';
import {ResumeFields} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/resume-fields';
import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
  type ToastSemanticType,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {useApi} from 'src/hooks/useApi';
import css from './unparsed-resume-panel.css';


type UnparsedResumePanelProps = {
  isOpen: boolean;
  handleClose: () => void;
  unparsedResume?: number;
};

export const UnparsedResumePanel = ({
  isOpen,
  handleClose,
  unparsedResume,
}: UnparsedResumePanelProps) => {
  const {t, makeKey} = useLabelTranslation();
  const jobInformation = useSelector(selectJobInformation);
  const applicantDetails = useSelector(selectApplicantInformation);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSkipping, setIsSkipping] = React.useState(false);

  const interviewFlowData = useSelector(selectJobInterviewFlow);
  const [unparsedResumeId, setUnparsedResumeId] = React.useState(
    unparsedResume || null,
  );
  const [searchJobTextOrCode, setSearchJobTextOrCode] = React.useState('');
  const [selectedJobId, setSelectedJobId] = React.useState('');
  const [selectedStageId, setSelectedStageId] = React.useState('');
  const dispatch = useDispatch();

  const [resumeData, setResumeData] = React.useState<any>(null);
  const filteredJobList = useSelector(selectFilteredJobList);
  const [isDisable, setIsDisable] = React.useState(true);
  const [isDeleting, setIsDeleting] = React.useState(false);
  // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HIERARCHY_LEVEL[]'. | TS7006 - Parameter 'job' implicitly has an 'any' type.
  const filteredJobListOptions = filteredJobList?.content.map((job) => ({
    key: job.id,
    label: job.title,
  }));
  const summary = jobInformation?.summary ?? {};
  const {unparsedCount} = summary;

  const [resumeHistory, setResumeHistory] = React.useState([]);

  React.useEffect(() => {
    const fetchUnparsedResume = async () => {
      try {
        setIsLoading(true);
        const payload = {
          jobId: jobInformation.id,
          afterCandidateId: 0,
        } as const;
        const data = await dispatch(
          reduxApi.get(`api/unparsed-resume-next`, payload, {
            apiPath: '/trmapi/',
          }),
        );
        setUnparsedResumeId(data);
        // @ts-expect-error - TS2322 - Type 'ThunkAction<any>' is not assignable to type 'never'.
        setResumeHistory([data]);
        // handleUnparsedResumeRedirect(data);
      } catch (error: any) {
        console.error('Failed to fetch interview data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUnparsedResume();
  }, [dispatch, jobInformation]);

  const isApplicantDetailsLoading = useInflight(
    getApplicantInformation(unparsedResumeId),
  );
  React.useEffect(() => {
    if (unparsedResumeId) {
      dispatch(getApplicantInformation(unparsedResumeId));
    }
  }, [unparsedResumeId, dispatch]);

  React.useEffect(() => {
    if (selectedJobId) {
      dispatch(getInterviewFlow(selectedJobId));
    }
  }, [selectedJobId, dispatch]);

  const tempFilter = React.useMemo(
    () => ({
      publicAccess: false,
      titleOrCode: searchJobTextOrCode,
      templateName: '',
      pageSize: 50,
      page: 0,
      jobStatuses: ['OPEN'],
      referralActive: false,
      template: false,
    }),
    [searchJobTextOrCode],
  );

  const debouncedJobSearch = React.useCallback(
    debounce(() => {
      dispatch(getFilteredJob({...tempFilter}));
    }, 300),
    [dispatch, tempFilter],
  );
  React.useEffect(() => {
    debouncedJobSearch();
  }, [tempFilter, debouncedJobSearch]);

  const handleSkip = async () => {
    try {
      setIsSkipping(true);
      const payload = {
        jobId: jobInformation.id,
        afterCandidateId: unparsedResumeId,
      } as const;
      const data = await dispatch(
        reduxApi.get(`api/unparsed-resume-next`, payload, {
          apiPath: '/trmapi/',
        }),
      );
      if (data !== -1) {
        setUnparsedResumeId(data);
        // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => ThunkAction<any>[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
        setResumeHistory((prev) => [...prev, data]);
      } else {
        toastApi.show(
          <Toast semantic="information">
            <ToastTitle>
              {t('No_more_unparsed_resumes', 'No more unparsed resumes')}
            </ToastTitle>
          </Toast>,
        );
        handleClose();
      }
    } catch (error: any) {
      console.error('Failed to fetch unparsed resume:', error);
      toastApi.show(
        <Toast semantic="danger">
          <ToastTitle>
            {t('Failed_to_skip_resume', 'Failed to skip resume')}
          </ToastTitle>
        </Toast>,
      );
    } finally {
      setIsSkipping(false);
    }
  };

  const handleBack = async () => {
    if (resumeHistory.length > 1) {
      const newHistory = resumeHistory.slice(0, -1);
      const previousResumeId = newHistory[newHistory.length - 1];
      setResumeHistory(newHistory);
      setUnparsedResumeId(previousResumeId);
      dispatch(getApplicantInformation(previousResumeId));
    } else {
      toastApi.show(
        <Toast semantic="information">
          <ToastTitle>
            {t('This_is_the_first_resume', 'This is the first resume')}
          </ToastTitle>
        </Toast>,
      );
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(
        reduxApi.post(
          `api/parse-resume-manual`,
          {
            candidateId: applicantDetails.id,
            isDeleted: true,
            prevJobId: jobInformation.id,
          },
          {},
          {
            apiPath: '/trmapi/',
          },
        ),
      );
      toastApi.show(
        <Toast semantic="success">
          <ToastTitle>{`${t(
            'Resume_Deleted_successfully',
            'Resume Deleted successfully',
          )}!`}</ToastTitle>
        </Toast>,
      );
      // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'.
      dispatch(getJobInformation(jobInformation.id));
    } catch (error: any) {
      console.error('Failed to delete and fetch next resume:', error);
      toastApi.show(
        <Toast semantic="danger">
          <ToastTitle>{`${t(
            'Unable_to_Delete_Resume',
            'Unable to Delete Resume',
          )}!`}</ToastTitle>
        </Toast>,
      );
      setIsDeleting(false);
    } finally {
      setIsDeleting(false);
      handleClose();
    }
  };

  const handleSaveAndResolve = async () => {
    try {
      setIsSubmitting(true);
      const payload = {
        parsedResume: resumeData,
        candidateId: applicantDetails.id,
        isDeleted: false,
        performJobStep: jobInformation.id ? true : false,
        stageId: selectedStageId,
        jobId: selectedJobId,
        prevJobId: jobInformation.id,
      } as const;
      await dispatch(
        reduxApi.post(
          `api/parse-resume-manual`,
          payload,
          {},
          {
            apiPath: '/trmapi/',
          },
        ),
      );
      toastApi.show(
        <Toast semantic="success">
          <ToastTitle>{`${t(
            'Resume_Parsed_successfully',
            'Resume Parsed successfully',
          )}!`}</ToastTitle>
        </Toast>,
      );
      // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'string'.
      dispatch(getJobInformation(jobInformation.id));
      handleClose();
    } catch (error: any) {
      toastApi.show(
        <Toast semantic="danger">
          <ToastTitle>
            {t(
              makeKey(error.responseBody?.error?.message || ''),
              error.responseBody?.error?.message || '',
            ) || `${t('Unable_to_parse_Resume', 'Unable to parse Resume')}!`}
          </ToastTitle>
        </Toast>,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (interviewFlowData?.stages) {
      const screeningStage = interviewFlowData.stages.find(
        (stage) => stage.stageType === 'SCREENING',
      );
      if (screeningStage) {
        // @ts-expect-error - TS2345 - Argument of type 'number' is not assignable to parameter of type 'SetStateAction<string>'.
        setSelectedStageId(screeningStage.id);
      }
    }
  }, [interviewFlowData]);

  return (
    <Panel
      anchor="right"
      onClose={handleClose}
      classNames={{content: css.panelContainer}}
      tapOutsideToClose
      isOpen={isOpen}
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={handleClose} size="medium">
        <SubTitleLarge>{t('Unparsed_Resume', 'Unparsed Resume')}</SubTitleLarge>
      </PanelHeader>
      {isLoading || isApplicantDetailsLoading ? (
        <CircularLoader className={css.loader} size="large" />
      ) : (
        <PanelBody>
          <div className={css.unparsedResumePanel}>
            <div className={css.leftPanel}>
              <ResumeCard applicantId={Number(unparsedResumeId)} />
            </div>
            <div className={css.rightPanel}>
              <ResumeFields
                setResumeData={setResumeData}
                setIsDisable={setIsDisable}
              />
              <div className={css.applicationContainer}>
                <BodyMediumBold>
                  {t(
                    'Step_2_Take_Decision_on_Application',
                    'Step 2 : Take Decision on Application',
                  )}{' '}
                </BodyMediumBold>
                <div className={css.twoInputContainer}>
                  <SimpleTypeahead
                    label={t('Select_a_Job', 'Select a Job')}
                    placeholder={t(
                      'Search_by_Job_Title_or_Code',
                      'Search by Job Title or Code',
                    )}
                    typeaheadInputText={searchJobTextOrCode}
                    options={filteredJobListOptions}
                    onSearch={(e) => setSearchJobTextOrCode(e.target.value)}
                    onSelect={(option) => {
                      setSelectedJobId(option.key);
                    }}
                    menuOpenOffset={0}
                    onClear={() => setSelectedJobId('')}
                    // isLoading={loadingFilteredJobList}
                  />
                  <SimpleDropdown
                    label={t('Stage', 'Stage')}
                    placeholder={t('Select_Stage', 'Select Stage')}
                    options={interviewFlowData?.stages
                      .slice(0, -1)
                      .map((stage: any) => ({
                        key: stage.id.toString(),
                        label: stage.name,
                      }))}
                    selectedKeys={
                      selectedStageId ? [selectedStageId.toString()] : []
                    }
                    onChange={(option) => setSelectedStageId(option.key)}
                    disabled={!selectedJobId}
                  />
                </div>
              </div>
            </div>
          </div>
        </PanelBody>
      )}
      <PanelFooter classNames={{actions: css.footerActions}}>
        <div className={css.buttonContainer}>
          <div className={css.buttonWrapper}>
            <Button
              iconRightName="trash"
              onClick={handleDelete}
              isLoading={isDeleting}
              classNames={{
                wrapper: css.trashButton,
                icon: css.trashIcon,
              }}
            />
          </div>
          <div className={css.buttonWrapper}>
            {resumeHistory.length > 1 && (
              <Button
                type="tertiary"
                onClick={handleBack}
                children={t('Previous', 'Previous')}
              />
            )}

            {unparsedCount > 1 && (
              <Button
                type="tertiary"
                onClick={handleSkip}
                children={t('Skip', 'Skip')}
                isLoading={isSkipping}
              />
            )}
            <Button
              type="primary"
              onClick={handleSaveAndResolve}
              isLoading={isSubmitting}
              disabled={isDisable}
              children={t('Save_Resolve', 'Save & Resolve')}
            />
          </div>
        </div>
      </PanelFooter>
    </Panel>
  );
};
